import $ from 'jquery';
import _ from 'lodash';

type EventOptions = {
  event: string;
  selector: string;
};

type EventListener = (event: JQuery.Event, ...args: unknown[]) => void;

function generateEventName(eventOptions: EventOptions): string {
  return `${eventOptions.event}-${eventOptions.selector}`;
}

export function on(eventOptions: EventOptions) {
  return function (
    target: JQueryView,
    propertyKey: string,
    descriptor: PropertyDescriptor,
  ) {
    if (!target.events) {
      target.events = {};
    }

    if (_.isFunction(target.events)) {
      throw new Error(
        'The on decorator is not compatible with an events method',
      );
    }

    if (!eventOptions) {
      throw new Error('The on decorator requires an eventOptions argument');
    }

    target.events[generateEventName(eventOptions)] = {
      ...eventOptions,
      listenerName: propertyKey,
    };

    return descriptor;
  };
}

export default class JQueryView {
  el: string;
  $el: JQuery;
  cid: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;

  constructor(el: string) {
    this.el = el;
    this.$el = $(el);
    this.cid = _.uniqueId('c');
    this.delegateEvents();
  }

  delegateEvents() {
    if (this.events == null) {
      return;
    }

    Object.keys(this.events).forEach(key => {
      const { event, selector, listenerName } = this.events[key];
      const listener = _.isFunction(listenerName)
        ? (listenerName as EventListener)
        : (this[listenerName as string] as EventListener);
      if (listener) this.delegate(event, selector, _.bind(listener, this));
    });
  }

  undelegateEvents() {
    if (this.events == null) {
      return;
    }

    Object.keys(this.events).forEach(key => {
      const { event, selector, listenerName } = this.events[key];
      const listener = _.isFunction(listenerName)
        ? listenerName
        : this[listenerName];
      if (listener) this.undelegate(event, selector);
    });
  }

  delegate(eventName: string, selector: string, listener: EventListener) {
    if (selector === 'self') {
      this.$el.on(eventName, listener);
    } else {
      this.$el.on(`${eventName}.delegateEvents${this.cid}`, selector, listener);
    }
  }

  undelegate(eventName: string, selector: string) {
    if (selector === 'self') {
      this.$el.undelegate(eventName);
    } else {
      this.$el.undelegate(selector, `${eventName}.delegateEvents${this.cid}`);
    }
  }

  destroy() {
    this.undelegateEvents();
  }
}
